<script setup>
import { inject, ref, computed, onMounted, onServerPrefetch } from 'vue'
import { useHead } from '@unhead/vue'
import { getCategoryMpm } from '@/api/article.js'
import SmallCard from '../components/SmallCard/index.vue'
import Mpm from './components/Mpm/index.vue'
import Mpm2 from './components/Mpm2/index.vue'
import CategoryModel from './components/CategoryModel/index.vue'
import CategoryAnchor from './components/CategoryAnchor/index.vue'
import { checkVideo } from '@/utils'
// import { info } from 'autoprefixer'

const size = inject('size')
const setAdParams = inject('setAdParams')
const isMobile = computed(() => ['s', 'xs'].includes(size.value))
const categoryInfo = inject('categoryInfo')
const sublist = computed(() => categoryInfo.value.children || [])
// 视频分类使用 position 12，文章分类使用 position 7
const adPosition = computed(() => (categoryInfo?.value?.is_video ? 12 : 7))
const isSubEmpty = computed(() => sublist.value.length === 0)

// 轮播图
const mpmloading = ref(true)
const mpmList = ref([])
const mpmBanner = ref([])
const mpmType = ref(1)
const isMpmEmpty = computed(() =>
  mpmType.value === 1 ? bigMpm.value.length === 0 : mpmBanner.value.length === 0
)
const bigMpm = computed(() => {
  // let num = 2
  // if (isMobile.value || mpmList.value.length <= 1) return mpmList.value
  // if (mpmList.value.length < 3) num = 1
  if (isMobile.value) return mpmList.value
  if (mpmList.value.length < 3) return []
  return mpmList.value.slice(0, 1).concat(mpmList.value.slice(3))
})
const smallMpm = computed(() => {
  // if (mpmList.value.length <= 1) return []
  // if (mpmList.value.length < 3) return mpmList.value.slice(-1)
  if (mpmList.value.length < 3) return []
  return mpmList.value.slice(1, 3)
})
const fetchMpm = async () => {
  try {
    const res = await getCategoryMpm({ category_path: categoryInfo.value.path })
    if (res.code !== 0) throw new Error(res.message)
    mpmList.value = res.data?.mpm_list || []
    mpmBanner.value = res.data?.mpm_banner || []
    mpmType.value = res.data?.mpm_type || 1
    mpmloading.value = false
  } catch (error) {
    mpmloading.value = false
  }
}

const scriptData = computed(() => {
  const script = [
    {
      id: 'category-ld-json',
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '主頁',
            item: {
              '@type': 'Thing',
              '@id': import.meta.env.SSR ? import.meta.env.VITE_HOST_URL : location.origin
            }
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: categoryInfo.value.title,
            item: {
              '@type': 'Thing',
              '@id': categoryInfo.value.link
            }
          }
        ]
      })
    }
  ]
  return { script }
})
const { patch } = useHead(scriptData)

onMounted(fetchMpm)
onServerPrefetch(() => {
  fetchMpm()
  patch(scriptData.value)
})

setAdParams({ content_type: 'content', main_category_path: categoryInfo?.value?.path || 'x' })

const isAllEmpty = computed(() => !mpmloading.value && isMpmEmpty.value && isSubEmpty.value)
</script>

<template>
  <div
    class="tvb-article-main-category"
    :class="[
      { 'error-page': isAllEmpty },
      { 'mpm-type_2': mpmType === 2 },
      { 'video-category': categoryInfo.is_video },
      size
    ]"
  >
    <template v-if="isAllEmpty">
      <tvb-empty icon="img_article_deleted" des="抱歉，找不到相關資料"></tvb-empty>
    </template>
    <template v-else>
      <category-anchor :sublist="sublist" :size="size" v-if="categoryInfo.is_video" />
      <div
        class="tvb-article-main-category-container"
        :class="{ 'video-category': categoryInfo.is_video }"
      >
        <template v-if="mpmloading ? true : !isMpmEmpty">
          <div v-if="mpmType === 1" class="main-category-header">
            <div class="main-category-mpm">
              <div
                v-if="mpmloading"
                class="tvb-skeleton"
                :class="{ 'tvb-hairline--surround': !isMobile }"
                style="height: 100%"
              ></div>
              <template v-else>
                <mpm :mpms="bigMpm" :size="size"></mpm>
              </template>
            </div>
            <template v-if="!isMobile">
              <template v-if="mpmloading">
                <div class="tvb-skeleton tvb-hairline--surround"></div>
                <div class="tvb-skeleton tvb-hairline--surround"></div>
              </template>
              <template v-else>
                <template v-for="(item, i) in smallMpm" :key="i">
                  <SmallCard
                    :img="item.image_info"
                    backgroundc="#F5F6F7"
                    :title="item.title"
                    :isVideo="checkVideo(item)"
                    :link="{ url: item.link, rel: item.link_rel, target: item.link_target }"
                  ></SmallCard>
                </template>
              </template>
            </template>
          </div>
          <div
            class="main-category-mpm2"
            :class="{ 'main-category-mpm2-onlyone': mpmBanner.length === 1 }"
            v-else
          >
            <mpm2 :mpms="mpmBanner" :size="size"></mpm2>
          </div>
        </template>

        <tvb-ads
          :ad-type="['s', 'xs'].includes(size) ? 'bn' : 'sb'"
          :position="adPosition"
          :key="['s', 'xs'].includes(size) ? 'bn' : 'sb'"
          :unit="`/${categoryInfo?.path}`"
          :style="{
            marginTop: ['s', 'xs'].includes(size) ? '32px' : '40px'
          }"
          :params="{
            content_type: 'content',
            main_category_path: categoryInfo?.path
          }"
        />
        <div v-if="!isSubEmpty" class="main-category-content">
          <template v-for="(item, i) in sublist" :key="item.id">
            <category-model
              :class="{ frist: isMobile ? i === 0 : i === 0 && isMpmEmpty && !mpmloading }"
              :size="size"
              :category-id="item.id"
              :category-is-video="categoryInfo?.is_video"
              :category-path="item.path"
              :main-category-path="categoryInfo?.path"
              :category-title="item.title"
              :title="item.title"
              :link="{ url: item.link }"
            ></category-model>
            <tvb-ads
              v-if="(i + 1) % 1 === 0"
              :style="{
                marginTop: ['s', 'xs'].includes(size) ? '32px' : '40px'
              }"
              :ad-type="['s', 'xs'].includes(size) ? 'bn' : 'sb'"
              :position="adPosition"
              :unit="`/${categoryInfo?.path}`"
              :params="{
                content_type: 'content',
                main_category_path: categoryInfo?.path || 'x'
              }"
            />
          </template>
        </div>
      </div>
      <tvb-ads
        :position="adPosition"
        :ad-type="'to'"
        :unit="`/${categoryInfo?.path}`"
        :params="{
          content_type: 'content',
          main_category_path: categoryInfo?.path || 'x'
        }"
      />
      <!-- Sky Scraper 浮动侧边栏广告已移至 layout/index.vue 统一管理 -->
    </template>
  </div>
</template>

<style lang="scss" scoped>
.tvb-article-main-category {
  &.video-category {
    margin-top: -8px;
  }
  .tvb-empty {
    height: 500px;
  }
  &.s,
  &.xs {
    padding-top: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    .tvb-empty {
      height: 400px;
    }
  }
  &.mpm-type_2.m {
    padding-left: 0 !important;
    padding-right: 0 !important;
    .main-category-content {
      padding-right: 32px;
      padding-left: 32px;
    }
  }

  .tvb-article-main-category-container {
    max-width: var(--tvb-article-max-width);
    margin: 0 auto;
    padding-top: 4px;
    &.video-category {
      padding-top: 0;
    }
  }

  .main-category-header {
    --gap: 16px;
    --columns: 3;
    gap: var(--gap);
    display: grid;
    grid-template-columns: repeat(var(--columns), 1fr);
    .tvb-skeleton {
      &:first-child {
        border-radius: 20px;
        --tvb-hairline-border-radius: calc(2 * 20px);
      }
      &:not(:first-child) {
        border-radius: 16px;
        --tvb-hairline-border-radius: calc(2 * 16px);
      }
      // overflow: hidden;
    }
    .main-category-mpm {
      grid-column: 1/3;
      grid-row: 1/3;
      aspect-ratio: calc(848px / 477px);
    }
  }

  .main-category-mpm2 {
    aspect-ratio: calc(1280px / 477px);
  }

  .main-category-content {
    .frist {
      margin-top: 32px - 20px;
    }
  }

  &.m,
  &.s,
  &.xs {
    // 单个换比例
    .main-category-mpm2.main-category-mpm2-onlyone {
      aspect-ratio: calc(1053px / 666px);
      @supports not (aspect-ratio: calc(1053px / 666px)) {
        position: relative;
        &::before {
          content: '';
          float: left;
          padding-top: calc((666px / 1053px) * 100%);
        }
        &::after {
          content: '';
          display: block;
          clear: both;
        }
        .tvb-main-category-mpm2 {
          position: absolute;
          top: 0;
        }
      }
    }
  }

  &.s,
  &.xs {
    .main-category-header {
      --columns: 1;
      .tvb-skeleton {
        border-radius: 16px;
      }
      .main-category-mpm {
        aspect-ratio: calc(390px / 230px);
        @supports not (aspect-ratio: calc(390px / 230px)) {
          position: relative;
          &::before {
            content: '';
            float: left;
            padding-top: calc((230px / 390px) * 100%);
          }
          &::after {
            content: '';
            display: block;
            clear: both;
          }
          .tvb-main-category-mpm {
            position: absolute;
            top: 0;
          }
        }
      }
    }

    .main-category-mpm2 {
      aspect-ratio: calc(390px / 246px);
      @supports not (aspect-ratio: calc(390px / 246px)) {
        position: relative;
        &::before {
          content: '';
          float: left;
          padding-top: calc((246px / 390px) * 100%);
        }
        &::after {
          content: '';
          display: block;
          clear: both;
        }
        .tvb-main-category-mpm2 {
          position: absolute;
          top: 0;
        }
      }
      &.main-category-mpm2-onlyone {
        margin-top: 12px;
        margin-left: 12px;
        margin-right: 12px;
      }
    }

    .main-category-content {
      .frist {
        margin-top: 24px;
      }
    }
  }

  &.m {
    .main-category-mpm2 {
      aspect-ratio: calc(1024px / 605px);
      @supports not (aspect-ratio: calc(1024px / 605px)) {
        position: relative;
        &::before {
          content: '';
          float: left;
          padding-top: calc((605px / 1024px) * 100%);
        }
        &::after {
          content: '';
          display: block;
          clear: both;
        }
        .tvb-main-category-mpm2 {
          position: absolute;
          top: 0;
        }
      }
      &.main-category-mpm2-onlyone {
        margin-left: 32px;
        margin-right: 32px;
      }
    }
  }
}
</style>
